// CloudKeeper Org Services Portal — Amazon Detective API client.
// Uses portalFetch from portalApi.js (JWT stored as portal_token).
// Mirrors macieApi.js / inspectorApi.js: a BASE const over portalFetch, and
// every call resolves to the {success,data} envelope.
//
// portalFetch gates on `response.ok`, so the 201 that POST /config, POST /refresh
// and POST /roles/status answer resolves exactly like a 200 — no call here needs a
// status special case. Any non-2xx instead REJECTS with a BARE Error carrying only
// `data.message || data.error`: there is no status code on the thrown Error, so a
// caller cannot tell a 404 from a 500 from a 412. Two rules follow, and they run
// through every Detective page:
//
//   * branch on PAYLOAD SHAPE, never on status — a null `data` means "not
//     configured", a rejection means a real failure;
//   * where a status is part of an endpoint's CONTRACT rather than a fault, read
//     the response here instead of going through portalFetch. There is exactly one
//     such case, the 422 that carries `missing[]`, and it is handled below.
//
// The resource is `/detective/findings` even though AWS calls these
// investigations. The name was chosen so the shared readers (FindingsList,
// FindingDetail, NormalizedFinding) need no Detective special case; the model is
// DetectiveFinding for the same reason.
import { portalFetch, getPortalToken, removePortalToken } from './portalApi';
// portalApi.js does not re-export API_BASE_URL — it imports it from the same
// module below. Reading it from the canonical source here is what api.js and
// jitApi.js already do, and it avoids duplicating the value.
import { API_BASE_URL } from '../utils/constants';

const BASE = '/api/v1/securityhub/detective';

// --- availability (DD4) ---

// Resolves with { available, reason }. Deliberately its own endpoint rather than a
// field on getDetectiveConfig: it must answer BEFORE and INDEPENDENTLY of whether
// this tenant has ever configured Detective, because the landing page has to be
// able to say "this installation cannot offer Detective" with no config to read.
//
// Callers must short-circuit only on `available === false`, never on
// `!available` — a call that FAILED leaves `available` undefined, and treating
// that as unavailable states a reason to the operator that is not true.
export const getDetectiveAvailability = () =>
  portalFetch(`${BASE}/availability`);

// --- config ---

// Resolves with `data: null` (HTTP 200) for a tenant that has never enabled
// Detective. That is the setup-wizard signal, NOT an error — a rejection here
// means a real failure and must be surfaced as one.
export const getDetectiveConfig = () =>
  portalFetch(`${BASE}/config`);

// The 422 fallback sentence. It is deliberately byte-identical to the one the
// backend puts in the 422 body so the wizard and the server never disagree about
// what is being asked for, and it says "root user email" rather than "email":
// AWS matches this value against the account's actual AWS root user, and an
// operator who reads "email" reaches for the account owner's work address, which
// resolves, validates, and then fails minutes later as VERIFICATION_FAILED. The
// wizard's step-3 field label uses the same words.
const MISSING_EMAILS_MESSAGE =
  'Some member accounts need an AWS root user email address before enrollment';

// A body this path can always read. A gateway timeout or proxy error answers with
// HTML rather than JSON, and portalFetch lets that SyntaxError escape as
// "Unexpected token '<'". This helper exists precisely to READ a body, so it has
// to survive one that is not JSON — and the 504 on the root-email pre-flight is a
// failure this fleet has already produced in production.
const readJsonBody = (res) => res.json().catch(() => ({}));

// The one place in the portal that does not go through portalFetch.
//
// Both enrollment endpoints answer 422 with a `missing[]` array naming the
// accounts whose AWS root user email could not be resolved. That is not a fault —
// it is a step in the wizard, and the wizard needs the array to send the operator
// back with exactly those accounts pre-filled. portalFetch would collapse it into
// a bare Error carrying only `data.error`, and the array would be gone; a generic
// "Request failed" about accounts the operator cannot identify is a dead end.
//
// Everything else about portalFetch's contract is preserved: the 401 token purge
// and hard redirect, the Bearer header, the JSON content type, and the
// {success,data} envelope on success.
//
// CONTRACT: on 422 this REJECTS (it does not resolve) with an Error carrying
// `err.missingEmails` — an array of { accountId, email, resolved, reason }.
// Rejecting rather than returning is what stops a caller that forgets to check
// from treating a refused enrollment as a success. A caller branches on
// `err.missingEmails?.length`, never on a status code, because there is none;
// a 422 whose body carries no usable array therefore falls through to the
// caller's generic error branch, which is the correct degradation.
const postExpectingMissingEmails = async (path, body) => {
  const token = getPortalToken();
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });
  // Checked before the body is read, exactly as portalFetch does: an expired
  // session must purge the stale token, not merely navigate away from it.
  if (res.status === 401) {
    removePortalToken();
    window.location.href = '/services';
    throw new Error('Authentication required');
  }
  const data = await readJsonBody(res);
  if (res.status === 422) {
    const err = new Error(data.error || MISSING_EMAILS_MESSAGE);
    err.missingEmails = Array.isArray(data.missing) ? data.missing : [];
    throw err;
  }
  // Byte-identical to portalFetch's own rejection, so every other failure reads
  // the same way whichever transport produced it.
  if (!res.ok) throw new Error(data.message || data.error || 'Request failed');
  return data;
};

// body: { adminAccountId, homeRegion, linkingRegions, memberAccountIds, memberEmails }
// Also answers 412 with the DD4 availability sentence when this installation
// cannot offer Detective — the UI gate is not a security boundary. That arrives
// as an ordinary rejection whose message IS the sentence to display.
export const enableDetective = (body) =>
  postExpectingMissingEmails(`${BASE}/config`, body);

export const disableDetective = () =>
  portalFetch(`${BASE}/config`, { method: 'DELETE' });

export const updateDetectiveRegions = (linkingRegions) =>
  portalFetch(`${BASE}/config/regions`, {
    method: 'PUT',
    body: JSON.stringify({ linkingRegions }),
  });

// --- members ---

export const listDetectiveMembers = () =>
  portalFetch(`${BASE}/members`);

// body: { accountIds, memberEmails }. Same 422 contract as enableDetective — an
// account added later can fail email resolution exactly as an initial one can.
export const addDetectiveMembers = (body) =>
  postExpectingMissingEmails(`${BASE}/members`, body);

// --- member emails ---
//
// Each account's root-email lookup is an independent prism-ops round-trip on the
// backend. Resolving a large selection in one request exceeds the gateway timeout
// — a 504 this fleet has already produced on /member-emails/resolve — so the
// request is split into small batches fired SEQUENTIALLY, the next starting as
// soon as the previous returns. Each request then stays well under the timeout no
// matter how many accounts are selected.
//
// Sequential and not parallel is the point: the goal is to keep each REQUEST
// short, and firing every batch at once simply recreates the load the backend's
// bounded pool exists to cap. The batch size matches guarddutyApi.js and
// macieApi.js, and it matches what the shipped documentation already tells
// operators to do by hand ("add member accounts in batches of 5 or fewer").
//
// It matters more here than for the siblings: Detective permits 1,200 member
// accounts on one behavior graph, which is 240 sequential requests, and nothing
// on the backend caches a resolved address, so every retry redoes every lookup.
const RESOLVE_CHUNK_SIZE = 5;

// Resolves with { data: { results }, errors } — `data.results` is the shape
// guarddutyApi.js and macieApi.js already return, so existing readers need no
// change, and `errors` is additive.
//
// Two behaviours the siblings do not have, both required at Detective's scale:
//
//   * onProgress(done, total) fires after every chunk. At 240 chunks an
//     unlabelled spinner is indistinguishable from a hang, and an operator who
//     reloads loses the whole wizard.
//   * a chunk that REJECTS does not abort the run. Its accounts are recorded as
//     unresolved and the loop continues, because failing the whole call throws
//     away every address that did resolve, and the per-account text fields are
//     already the recovery for an unresolved account. The rejections are not
//     swallowed — each one lands in `errors` with the accounts it covered, and
//     each account gets a `reason` the wizard renders beside its field.
export const resolveDetectiveMemberEmails = async (accountIds, options = {}) => {
  const { onProgress } = options;
  const ids = accountIds || [];
  const results = [];
  const errors = [];
  for (let i = 0; i < ids.length; i += RESOLVE_CHUNK_SIZE) {
    const chunk = ids.slice(i, i + RESOLVE_CHUNK_SIZE);
    try {
      // eslint-disable-next-line no-await-in-loop -- intentional: one batch at a time
      const res = await portalFetch(`${BASE}/member-emails/resolve`, {
        method: 'POST',
        body: JSON.stringify({ accountIds: chunk }),
      });
      results.push(...(res?.data?.results || []));
    } catch (e) {
      const reason = e?.message || 'Request failed';
      errors.push({ accountIds: chunk, message: reason });
      // Synthesised in the same shape the backend returns for an account it could
      // not resolve, so the caller has one list to render rather than two and a
      // failed chunk is indistinguishable from a chunk whose accounts simply had
      // no address on file — which is the outcome either way.
      chunk.forEach((accountId) => {
        results.push({ accountId, email: '', resolved: false, reason });
      });
    }
    if (onProgress) onProgress(Math.min(i + RESOLVE_CHUNK_SIZE, ids.length), ids.length);
  }
  return { data: { results }, errors };
};

// retry takes an OPTIONAL replacement address. The commonest reason to retry is
// VERIFICATION_FAILED, which means resolution SUCCEEDED and AWS rejected the
// result — so retrying with the same address fails identically. The backend uses
// a supplied value verbatim rather than re-resolving, which would return the same
// rejected address and loop forever. Omitting it keeps the cell's stored address.
export const retryDetectiveMember = (accountId, email) =>
  portalFetch(`${BASE}/members/retry`, {
    method: 'POST',
    body: JSON.stringify({ accountId, email }),
  });

// The ACCEPTED_BUT_DISABLED recovery: the account is in the graph but is being
// held out of ingestion. Deliberately NOT folded into retryDetectiveMember —
// re-running enrollment for a member that is already in the graph 409s, and the
// two are different operator intents that the audit log must be able to tell
// apart.
export const startDetectiveMonitoring = (accountId) =>
  portalFetch(`${BASE}/members/${encodeURIComponent(accountId)}/start-monitoring`, {
    method: 'POST',
  });

export const removeDetectiveMember = (accountId) =>
  portalFetch(`${BASE}/members/${encodeURIComponent(accountId)}`, {
    method: 'DELETE',
  });

// --- findings (AWS calls them investigations; see the header note) ---

export const listDetectiveFindings = (params = {}) => {
  const qs = new URLSearchParams();
  // severity and state are the only multi-valued filters; the handler splits them
  // on "," and drops blanks. accountId and entityType are SINGLE-valued —
  // comma-joining a list into either would be matched literally and return
  // nothing.
  //
  // severity accepts a sixth value beyond the five bands, UNRATED, which selects
  // the rows whose severity is empty. Those exist because the list refresh cannot
  // populate severity — it arrives only when someone opens a finding's detail —
  // so without the sentinel an operator who ticks all five bands gets a strictly
  // smaller list than the unfiltered one with nothing explaining the gap.
  // UNRATED is Prism's own word and is never sent to AWS.
  if (params.severity?.length) qs.set('severity', params.severity.join(','));
  // ACTIVE | ARCHIVED, read from the finding's State and never from its Status —
  // Status is the analysis run (RUNNING/FAILED/SUCCESSFUL), which is a different
  // axis. The backend rejects anything outside the two values rather than
  // building a match-nothing IN clause, so a typo is an error and not a silently
  // empty list.
  if (params.state?.length) qs.set('state', params.state.join(','));
  if (params.accountId) qs.set('accountId', params.accountId);
  // IAM_ROLE | IAM_USER — Detective's analogue of the other engines' `type`.
  if (params.entityType) qs.set('entityType', params.entityType);
  if (params.search) qs.set('search', params.search);
  // Cursor drives keyset pagination; dropping it pins every page to the first.
  if (params.cursor) qs.set('cursor', params.cursor);
  if (params.limit) qs.set('limit', String(params.limit));
  const q = qs.toString();
  return portalFetch(`${BASE}/findings${q ? `?${q}` : ''}`);
};

// Detective finding ids are numeric strings, not ARNs, so encoding is defensive
// here rather than load-bearing the way it is for Inspector — an unencoded
// Inspector ARN carries slashes that split the path and miss the single-segment
// route entirely. It is applied anyway for consistency with the siblings and so
// that a future id shape cannot quietly break the route.
export const getDetectiveFinding = (id) =>
  portalFetch(`${BASE}/findings/${encodeURIComponent(id)}`);

// --- refresh (the three-part polling contract) ---

// No "kind" discriminator in the body, unlike triggerMacieRefresh: Detective
// caches only findings, so there is nothing to choose between.
export const triggerDetectiveRefresh = () =>
  portalFetch(`${BASE}/refresh`, { method: 'POST' });

// Currently running refresh job for this tenant (data: null when none, including
// for an unconfigured tenant) — lets the dashboard re-attach its progress bar
// after navigation or a reload. Takes no `kind`, again unlike Macie's.
export const getDetectiveActiveRefreshJob = () =>
  portalFetch(`${BASE}/refresh/active`);

// Rejects once a job ages out of the table; poll loops should stop on rejection
// rather than retrying forever.
export const getDetectiveRefreshJob = (jobId) =>
  portalFetch(`${BASE}/refresh/${encodeURIComponent(jobId)}`);

// --- posture ---

// Powers the dashboard's coverage tiles. The headline is accounts and regions,
// never the investigation count: a healthy tenant that has never started an
// investigation legitimately reports zero, and a freshly refreshed one
// legitimately reports an all-zero severity strip.
export const getDetectiveSummary = () =>
  portalFetch(`${BASE}/summary`);

// --- role permissions ("Fix roles", props for RolePermissionsPanel) ---
// Prism keeps a scoped inline policy on each enrolled account's switch role. The
// scan is async: POST starts (or re-joins) a job, GET polls it. Fix is
// fire-and-forget — re-run a scan to see the outcome.

export const startDetectiveRolesScan = () =>
  portalFetch(`${BASE}/roles/status`, { method: 'POST' });

export const getDetectiveRolesJob = (jobId) =>
  portalFetch(`${BASE}/roles/status/${encodeURIComponent(jobId)}`);

export const fixDetectiveRoles = () =>
  portalFetch(`${BASE}/roles/fix`, { method: 'POST' });
